import { useCallback, useEffect, useState } from "react";

import { useApi, ApiError } from "../api/client";
import { buildMockActivity } from "./data/mockActivity";
import type { ActivityReport } from "./activityTypes";
import type { CohortFilters } from "./reportTypes";

/**
 * Loads de-identified assessment-activity aggregates for the current cohort
 * filter from GET /api/v1/reports/activity. Same gate + mock-fallback behaviour
 * as useReportData (the server enforces reports.view; 403 → forbidden; in dev a
 * missing endpoint falls back to the bundled mock; VITE_REPORTS_USE_MOCK forces
 * mock).
 */
export type ActivityDataState =
  | { kind: "loading" }
  | { kind: "ok"; data: ActivityReport; source: "api" | "mock" }
  | { kind: "forbidden" }
  | { kind: "error"; message: string; status?: number };

const USE_MOCK =
  (import.meta.env.VITE_REPORTS_USE_MOCK as string | undefined) === "true";

/** Stable empty default so callers that don't filter by completed-by don't
 *  create a fresh array each render (which would re-fire the load effect). */
const NO_COMPLETED_BY: readonly string[] = [];

function toQuery(f: CohortFilters, completedBy: readonly string[]): string {
  const params = new URLSearchParams();
  for (const v of f.location) params.append("location", v);
  for (const v of f.iwi) params.append("iwi", v);
  for (const v of f.service) params.append("service", v);
  for (const v of f.durationBand) params.append("duration_band", v);
  for (const v of completedBy) params.append("completed_by", v);
  const q = params.toString();
  return q ? `?${q}` : "";
}

export function useActivityData(
  filters: CohortFilters,
  completedBy: readonly string[] = NO_COMPLETED_BY,
): ActivityDataState {
  const callApi = useApi();
  const [state, setState] = useState<ActivityDataState>({ kind: "loading" });

  const load = useCallback(async () => {
    setState({ kind: "loading" });

    if (USE_MOCK) {
      setState({ kind: "ok", data: buildMockActivity(), source: "mock" });
      return;
    }

    try {
      const data = await callApi<ActivityReport>(
        `/reports/activity${toQuery(filters, completedBy)}`,
      );
      setState({ kind: "ok", data, source: "api" });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setState({ kind: "forbidden" });
        return;
      }
      if (import.meta.env.DEV) {
        setState({ kind: "ok", data: buildMockActivity(), source: "mock" });
        return;
      }
      if (err instanceof ApiError) {
        setState({ kind: "error", status: err.status, message: err.message });
      } else {
        const message = err instanceof Error ? err.message : "Unknown error";
        setState({ kind: "error", message });
      }
    }
    // callApi is a fresh closure each render; deliberately excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, completedBy]);

  useEffect(() => {
    void load();
  }, [load]);

  return state;
}
